import { useState } from "react";
import { PackingKit } from "~/data/packingKits.ts";
import { useSpace } from "~/providers/SpaceContext.ts";
import { useTemplate } from "~/providers/TemplateContext.ts";
import { MemberPackItem } from "~/types/MemberPackItem.ts";
import { NamedEntity } from "~/types/NamedEntity.ts";
import { PackItem } from "~/types/PackItem.ts";
import { AddItemDialog } from "./AddItemDialog.tsx";
import { HomeHeader } from "./HomeHeader.tsx";
import { ItemsList } from "./ItemsList.tsx";
import type { ListNotesState } from "./ListNotesSheet.tsx";
import { PanelShell } from "./PanelShell.tsx";
import { QuickAddRow } from "./QuickAddRow.tsx";
import { SpaceSheet } from "./SpaceSheet.tsx";
import { HOME_COPY } from "./styles.ts";
import { TextPromptDialog } from "./TextPromptDialog.tsx";
import { homeSpacing } from "./theme.ts";
import { ItemsSectionProps } from "./types.ts";
import type { FilterDialogState } from "./useFilterDialog.ts";
import type { SearchState } from "./useSearch.ts";

export type TextDialogState = {
  visible: boolean;
  value: string;
  error?: string | null;
  getError?: (text: string) => string | null;
  setValue: (text: string) => void;
  open: () => void;
  close: () => void;
  submitText: (text: string) => void;
  submit: () => void;
};

export type AddItemDialogState = {
  visible: boolean;
  initialCategory?: NamedEntity;
  open: (initialCategory?: NamedEntity) => void;
  close: () => void;
  submit: (
    itemName: string,
    category: NamedEntity | null,
    newCategoryName: string | null,
    keepOpen: boolean
  ) => Promise<NamedEntity>;
  onBrowseKits: () => void;
  kitPickerVisible: boolean;
  closeKitPicker: () => void;
  addKits: (kits: PackingKit[]) => Promise<void>;
};

export type ListHandlers = {
  onToggle: (item: PackItem) => void;
  onRenameItem: (item: PackItem, name: string) => void;
  onDeleteItem: (id: string) => void;
  onRenameCategory: (category: NamedEntity, name: string) => void;
  onToggleCategory: (items: PackItem[], checked: boolean) => void;
  onAssignMembers: (item: PackItem, members: MemberPackItem[]) => Promise<void>;
  onToggleMemberPacked: (item: PackItem, memberId: string) => void;
  onToggleAllMembers: (item: PackItem, checked: boolean) => void;
  onMoveCategory: (item: PackItem, categoryId: string) => void;
  onMoveItemsToCategory: (items: PackItem[], categoryId: string) => Promise<void>;
  onCopyToList: (item: PackItem, listId: string) => Promise<void>;
  onSortCategoryAlpha: (items: PackItem[]) => Promise<void>;
};

type ItemsPanelProps = ItemsSectionProps &
  ListHandlers & {
    allItems: PackItem[];
    list: NamedEntity;
    displayName: string;
    listImageUrl?: string;
    listImageLoading?: boolean;
    onListImagePress: () => void;
    onItemImagePress: (item: PackItem) => void;
    renameDialog: TextDialogState;
    addItemDialog: AddItemDialogState;
    filterDialog: FilterDialogState;
    search: SearchState;
    notesSheet: ListNotesState;
    onBulkEdit: () => void;
    bulkEditing: boolean;
    onUndo: () => void;
    canUndo: boolean;
  };

export const ItemsPanel = (props: ItemsPanelProps) => {
  const [spaceSheetVisible, setSpaceSheetVisible] = useState(false);
  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <PanelCard {...props} onSpacePress={() => setSpaceSheetVisible(true)} />
      <SpaceSheet visible={spaceSheetVisible} onClose={() => setSpaceSheetVisible(false)} />
    </div>
  );
};

const PanelCard = (props: ItemsPanelProps & { onSpacePress: () => void }) => (
  <PanelShell>
    <HeaderRow {...props} />
    <div style={{ display: "flex", flex: 1, minHeight: 0, flexDirection: "column", gap: homeSpacing.md }}>
      <QuickAddRow
        addDialog={props.addItemDialog}
        filterDialog={props.filterDialog}
        search={props.search}
        onNotes={props.notesSheet.open}
        hasNotes={!!props.list.notes}
        onBulkEdit={props.onBulkEdit}
        bulkEditing={props.bulkEditing}
        onUndo={props.onUndo}
        canUndo={props.canUndo}
      />
      <ItemsListView {...props} />
    </div>
    <RenameDialog dialog={props.renameDialog} />
    <AddItemDialogView {...props} />
  </PanelShell>
);

const HeaderRow = ({
  displayName,
  email,
  listImageUrl,
  listImageLoading,
  onListImagePress,
  renameDialog,
  onProfile,
  onSpacePress,
}: ItemsPanelProps & { onSpacePress: () => void }) => {
  const { profile } = useSpace();
  return (
    <HomeHeader
      title={displayName}
      email={email}
      profileImageUrl={profile?.imageUrl}
      leftImageUrl={listImageUrl}
      leftImageLoading={listImageLoading}
      hideLeftImagePlaceholder={profile?.hideImagePlaceholder ?? false}
      onPressLeftImage={onListImagePress}
      onPressTitle={renameDialog.open}
      onSpacePress={onSpacePress}
      onProfile={onProfile}
    />
  );
};

const RenameDialog = ({ dialog }: { dialog: TextDialogState }) => (
  <TextPromptDialog
    visible={dialog.visible}
    title={HOME_COPY.renameListPrompt}
    confirmLabel={HOME_COPY.renameListConfirm}
    value={dialog.value}
    error={dialog.error}
    getError={dialog.getError}
    onChange={dialog.setValue}
    onCancel={dialog.close}
    onSubmitText={dialog.submitText}
    onSubmit={dialog.submit}
  />
);

const AddItemDialogView = ({ addItemDialog, categoriesState, allItems, imagesState }: ItemsPanelProps) => (
  <AddItemDialog
    visible={addItemDialog.visible}
    initialCategory={addItemDialog.initialCategory}
    categories={categoriesState.categories}
    categoryImages={imagesState.images.filter((img) => img.type === "categories")}
    items={allItems}
    onCancel={addItemDialog.close}
    onSubmit={addItemDialog.submit}
    onBrowseKits={addItemDialog.onBrowseKits}
  />
);

const ItemsListView = ({
  categoriesState,
  itemsState,
  allItems,
  membersState,
  imagesState,
  lists,
  list,
  selection,
  search,
  addItemDialog,
  notesSheet,
  onToggle,
  onRenameItem,
  onDeleteItem,
  onRenameCategory,
  onToggleCategory,
  onAssignMembers,
  onToggleMemberPacked,
  onToggleAllMembers,
  onMoveCategory,
  onMoveItemsToCategory,
  onCopyToList,
  onSortCategoryAlpha,
  onItemImagePress,
  onShowChanges,
}: ItemsPanelProps) => {
  const memberImages = imagesState.images.filter((img) => img.type === "members");
  const categoryImages = imagesState.images.filter((img) => img.type === "categories");
  const itemImages = imagesState.images.filter((img) => img.type === "packItems");
  const { isTemplateList } = useTemplate();
  const isTemplate = isTemplateList(selection.selectedId);
  return (
    <ItemsList
      loading={categoriesState.loading || itemsState.loading}
      hasItems={itemsState.hasItems}
      filteredEmpty={itemsState.filteredEmpty === true}
      items={itemsState.items}
      allItems={allItems}
      categories={categoriesState.categories}
      members={membersState.members}
      memberImages={memberImages}
      categoryImages={categoryImages}
      itemImages={itemImages}
      memberInitials={membersState.memberInitials}
      memberNames={membersState.memberNames}
      lists={lists}
      currentListId={selection.selectedId}
      isTemplateList={isTemplate}
      search={search}
      notes={list.showNotes ? list.notes : undefined}
      onNotesPress={notesSheet.open}
      onToggle={onToggle}
      onRenameItem={onRenameItem}
      onDeleteItem={onDeleteItem}
      onOpenAddDialog={addItemDialog.open}
      onRenameCategory={onRenameCategory}
      onToggleCategory={onToggleCategory}
      onAssignMembers={onAssignMembers}
      onToggleMemberPacked={onToggleMemberPacked}
      onToggleAllMembers={onToggleAllMembers}
      onMoveCategory={onMoveCategory}
      onMoveItemsToCategory={onMoveItemsToCategory}
      onCopyToList={onCopyToList}
      onSortCategoryAlpha={onSortCategoryAlpha}
      onItemImagePress={onItemImagePress}
      onBrowseKits={addItemDialog.onBrowseKits}
      onAddKit={addItemDialog.addKits}
      onShowChanges={onShowChanges}
    />
  );
};
